import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RichTextEditor } from '../components/RichTextEditor';
import { ContentSidebar } from '../components/ContentSidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ProgressIndicator } from '../components/ProgressIndicator';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/ui/use-toast';
import { Edit2, Eye, Save, ChevronLeft, ChevronRight } from 'lucide-react';

interface Topic {
  id: string;
  slug: string;
  title: string;
  content: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  prerequisites?: string[];
  nextTopics?: string[];
}

// Mock topics data
const topics: Record<string, Topic> = {
  'arrays-introduction': {
    id: 'arrays-introduction',
    slug: 'arrays-introduction',
    title: 'Introduction to Arrays',
    category: 'Data Structures',
    difficulty: 'Beginner',
    content: `# Introduction to Arrays

Arrays are fundamental data structures that store elements in contiguous memory locations. They provide fast access to elements using indices and are widely used in programming.

## Key Concepts

1. **Indexing**: Arrays use zero-based indexing
2. **Contiguous Memory**: Elements are stored in consecutive memory locations
3. **Fixed Size**: Arrays have a fixed size in most programming languages

\`\`\`javascript
// Example array operations
const arr = [1, 2, 3, 4, 5];
arr.push(6);     // Add to end
arr.pop();       // Remove from end
arr.shift();     // Remove from start
arr.unshift(0);  // Add to start
\`\`\`

## Time Complexity

Common array operations and their time complexities:

- Access: O(1)
- Search: O(n)
- Insertion: O(n)
- Deletion: O(n)

> Understanding array operations' time complexity is crucial for writing efficient code.

## Common Patterns

Arrays are often used in various algorithmic patterns:

1. Two Pointer Technique
2. Sliding Window
3. Binary Search
4. Prefix Sum

## Practice Problems

Start with these problems to master array concepts:

1. Two Sum
2. Maximum Subarray
3. Container With Most Water`,
    nextTopics: ['array-operations']
  },
  'array-operations': {
    id: 'array-operations',
    slug: 'array-operations',
    title: 'Array Operations',
    category: 'Data Structures',
    difficulty: 'Beginner',
    content: `# Array Operations

Learn about common array operations and their implementations...`,
    prerequisites: ['arrays-introduction'],
    nextTopics: ['array-algorithms']
  },
  'array-algorithms': {
    id: 'array-algorithms',
    slug: 'array-algorithms',
    title: 'Array Algorithms',
    category: 'Data Structures',
    difficulty: 'Intermediate',
    content: `# Array Algorithms

Advanced algorithms and techniques using arrays...`,
    prerequisites: ['array-operations']
  }
};

export function TopicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [content, setContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { isAdmin } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (slug && topics[slug]) {
      setTopic(topics[slug]);
      setContent(topics[slug].content);
      setIsEditing(false);
    }
  }, [slug]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update the topics data
      if (topic) {
        topics[topic.slug] = {
          ...topic,
          content: content
        };
      }
      
      toast({
        title: "Success",
        description: "Content saved successfully",
      });
      setIsEditing(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save content",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSectionSelect = (section: any) => {
    if (section.slug) {
      navigate(`/codespace/${section.slug}`);
    }
  };

  const navigateToTopic = (slug: string) => {
    navigate(`/codespace/${slug}`);
  };

  if (!topic) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900">Topic not found</h2>
          <p className="mt-2 text-gray-600">The requested topic could not be found.</p>
          <button
            onClick={() => navigate('/content')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Content
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      <ResizablePanelGroup direction="horizontal">
        <ResizablePanel 
          defaultSize={25} 
          minSize={15} 
          maxSize={40}
          collapsible
          collapsedSize={0}
          onCollapse={() => setIsSidebarCollapsed(true)}
          onExpand={() => setIsSidebarCollapsed(false)}
        >
          <ContentSidebar
            onSectionSelect={handleSectionSelect}
            searchQuery=""
            onSearchChange={() => {}}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          />
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={75}>
          <div className="h-full flex flex-col overflow-hidden">
            <div className="p-6 bg-white border-b space-y-4">
              <div className="max-w-4xl mx-auto w-full">
                <Breadcrumbs 
                  items={[
                    { label: 'Content', href: '/content' },
                    { label: topic.category, href: `/content/${topic.category.toLowerCase()}` },
                    { label: topic.title }
                  ]} 
                />

                <div className="flex justify-between items-center mt-4">
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900">
                      {topic.title}
                    </h1>
                    <div className="mt-2 flex items-center space-x-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        topic.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                        topic.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {topic.difficulty}
                      </span>
                      <ProgressIndicator sectionId={topic.id} />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    {isAdmin && (
                      <button
                        onClick={() => setIsEditing(!isEditing)}
                        className="px-4 py-2 rounded-md flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        {isEditing ? (
                          <>
                            <Eye className="w-4 h-4" />
                            <span>Preview</span>
                          </>
                        ) : (
                          <>
                            <Edit2 className="w-4 h-4" />
                            <span>Edit</span>
                          </>
                        )}
                      </button>
                    )}
                    {isEditing && (
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 rounded-md flex items-center space-x-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        <span>{isSaving ? 'Saving...' : 'Save'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-white">
              <div className="max-w-4xl mx-auto px-6 py-8">
                <RichTextEditor 
                  content={content} 
                  onChange={setContent}
                  editable={isEditing}
                  onSave={handleSave}
                />

                <div className="mt-8 flex justify-between">
                  {topic.prerequisites?.[0] && (
                    <button
                      onClick={() => navigateToTopic(topic.prerequisites![0])}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Previous Topic</span>
                    </button>
                  )}
                  {topic.nextTopics?.[0] && (
                    <button
                      onClick={() => navigateToTopic(topic.nextTopics![0])}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                    >
                      <span>Next Topic</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}